import { UkDateInput, ukDate } from "@/components/UkDateInput";

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, TriangleAlert, Trash2, User, MapPin, Calendar } from "lucide-react";
import { statusTone, HeaderAccent } from "@/lib/status";

const TYPE_LABEL = { incident: "Incident", accident: "Accident", near_miss: "Near miss" };
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { type: "incident", severity: "low", client_id: "", carer_id: "", date: today(), location: "", description: "", action_taken: "" };

export default function Incidents() {
  const { isAdmin, user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");
  const location = useLocation();

  const load = () => api.get("/incidents").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (isAdmin) api.get("/staff").then((r) => setStaff(r.data.filter((s) => s.role === "carer"))).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (location.state?.reportForClient !== undefined) {
      setForm({ ...EMPTY, carer_id: isAdmin ? "" : user.id, client_id: location.state.reportForClient || "", date: location.state.date || today() });
      setOpen(true);
    }
    // eslint-disable-next-line
  }, [location.state]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Please describe what happened"); return; }
    try { await api.post("/incidents", form); toast.success("Incident reported"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const setStatusFor = async (id, status) => {
    try { await api.put(`/incidents/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (i) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/incidents/${i.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => filter === "all" ? items : items.filter((i) => i.status === filter), [items, filter]);
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Incidents & accidents</h1>
          <p className="mt-1 text-ink-soft">{isAdmin ? "All reported incidents across the service." : "Report and track incidents from your visits."}</p>
          <HeaderAccent />
        </div>
        <button onClick={() => { setForm({ ...EMPTY, carer_id: isAdmin ? "" : user.id }); setOpen(true); }} data-testid="report-incident-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
          <Plus size={18} /> Report incident
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "open", "under_review", "closed"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`incident-filter-${f}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium capitalize transition-colors ${filter === f ? "bg-brand text-white" : "border border-border bg-white text-ink-soft hover:bg-secondary"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map((i) => (
          <div key={i.id} data-testid={`incident-card-${i.id}`} className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-coral-light text-coral"><TriangleAlert size={20} /></div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-ink">{TYPE_LABEL[i.type]}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${statusTone(i.severity)}`}>{i.severity}</span>
                  </div>
                  <p className="text-xs text-ink-muted">{i.client_name || "No client"} · reported by {i.reported_by}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin ? (
                  <select value={i.status} onChange={(e) => setStatusFor(i.id, e.target.value)} data-testid={`incident-status-${i.id}`}
                    className="rounded-lg border border-border bg-white px-2 py-1 text-xs font-medium outline-none">
                    <option value="open">Open</option><option value="under_review">Under review</option><option value="closed">Closed</option>
                  </select>
                ) : (
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone(i.status)}`}>{i.status.replace("_", " ")}</span>
                )}
                {isAdmin && <button onClick={() => remove(i)} data-testid={`delete-incident-${i.id}`} className="text-ink-muted hover:text-coral"><Trash2 size={16} /></button>}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink-soft">
              <span className="flex items-center gap-1.5"><Calendar size={13} />{ukDate(i.date)}</span>
              <span className="flex items-center gap-1.5"><User size={13} />{i.carer_name}</span>
              {i.location && <span className="flex items-center gap-1.5"><MapPin size={13} />{i.location}</span>}
            </div>
            <p className="mt-3 text-sm text-ink"><b>What happened:</b> {i.description}</p>
            {i.action_taken && <p className="mt-1 text-sm text-ink-soft"><b>Action taken:</b> {i.action_taken}</p>}
          </div>
        ))}
        {filtered.length === 0 && <p className="py-12 text-center text-ink-muted">No incidents recorded.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <form onSubmit={save} data-testid="incident-form" className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl scrollbar-thin">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ink">Report incident / accident</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-sm font-medium text-ink">Type</label>
                <select data-testid="incident-type-select" className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="incident">Incident</option><option value="accident">Accident</option><option value="near_miss">Near miss</option>
                </select></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Severity</label>
                <select data-testid="incident-severity-select" className={input} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Client</label>
                <select className={input} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                  <option value="">Not client-specific</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              {isAdmin && (
                <div><label className="mb-1 block text-sm font-medium text-ink">Befriender involved</label>
                  <select className={input} value={form.carer_id} onChange={(e) => setForm({ ...form, carer_id: e.target.value })}>
                    <option value="">Me ({user.name})</option>
                    {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select></div>
              )}
              <div><label className="mb-1 block text-sm font-medium text-ink">Date</label>
                <UkDateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="incident-date-input" /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Location</label>
                <input className={input} placeholder="e.g. Client's kitchen" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-ink">What happened *</label>
              <textarea required rows={3} data-testid="incident-description-input" className={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-ink">Action taken</label>
              <textarea rows={2} className={input} value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} />
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button type="submit" data-testid="save-incident-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Submit report</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
